import { doc, setDoc, getDocs, collection, query, where, orderBy, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';

export interface Customer {
  id: string; // Firestore document ID
  uid: string; // Firebase Auth UID (should be the same as id)
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  provider: string;
  userType: 'customer' | 'admin';
  role: 'Admin' | 'Customer';
  createdAt: Date;
}

export interface ProductRequest {
  id: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  productId: string;
  productName: string;
  requestType: 'installation' | 'product_request';
  description: string;
  urgency: 'low' | 'normal' | 'high';
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

// Get all customers from Firestore
export const getCustomers = async (): Promise<Customer[]> => {
  try {
    logger.log('🔍 Getting customers from Firestore...');
    const usersCollection = collection(db, 'users');
    
    // Try querying with userType filter first
    let userSnapshot;
    try {
      const q = query(usersCollection, where('userType', '==', 'customer'));
      userSnapshot = await getDocs(q);
      logger.log('📊 Found customers with userType filter:', userSnapshot.docs.length);
    } catch (queryError: any) {
      logger.warn('⚠️ Query with userType filter failed, trying to get all users:', queryError?.message);
      // Fallback: Get all users and filter manually
      userSnapshot = await getDocs(usersCollection);
      logger.log('📊 Total users in collection:', userSnapshot.docs.length);
    }
    
    // Filter customers manually if needed
    const customerDocs = userSnapshot.docs.filter(doc => {
      const data = doc.data();
      return data.userType === 'customer' || data.role === 'Customer' || (!data.userType && !data.role);
    });
    
    logger.log('📊 Found customers:', customerDocs.length);
    customerDocs.forEach(doc => {
      const data = doc.data();
      logger.log('👤 Customer document:', {
        id: doc.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        userType: data.userType,
        role: data.role
      });
    });
    
    const customersList = customerDocs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        uid: doc.id, // Firebase Auth UID should be the same as Firestore document ID
        email: data.email || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phone: data.phone || '',
        provider: data.provider || 'email',
        userType: data.userType || 'customer',
        role: data.role || 'Customer',
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date())
      } as Customer;
    });
    
    logger.log('✅ Returning customers list:', customersList.map(c => ({ id: c.id, email: c.email })));
    return customersList;
  } catch (error: any) {
    logger.error('💥 Error getting customers:', error);
    logger.error('💥 Error details:', {
      message: error?.message,
      code: error?.code,
      stack: error?.stack
    });
    // Return empty array instead of throwing to prevent breaking the UI
    return [];
  }
};

// Get all product requests from Firestore
export const getProductRequests = async (): Promise<ProductRequest[]> => {
  try {
    logger.log('🔍 Getting product requests from Firestore...');
    const requestsCollection = collection(db, 'productRequests');
    const q = query(requestsCollection, orderBy('createdAt', 'desc'));
    const requestSnapshot = await getDocs(q);
    
    logger.log('📊 Found product requests:', requestSnapshot.docs.length);
    requestSnapshot.docs.forEach(doc => {
      const data = doc.data();
      logger.log('📋 Product request:', {
        id: doc.id,
        customerId: data.customerId,
        customerEmail: data.customerEmail,
        productName: data.productName,
        status: data.status
      });
    });
    
    const requestsList = requestSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as ProductRequest[];
    
    logger.log('✅ Returning product requests list:', requestsList.map(r => ({ id: r.id, customerId: r.customerId, customerEmail: r.customerEmail })));
    return requestsList;
  } catch (error) {
    logger.error('💥 Error getting product requests:', error);
    throw error;
  }
};

// Save a product request to Firestore
export const saveProductRequest = async (request: Omit<ProductRequest, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const requestRef = doc(collection(db, 'productRequests'));
    const newRequest: ProductRequest = {
      id: requestRef.id,
      createdAt: new Date(),
      updatedAt: new Date(),
      ...request,
    };
    await setDoc(requestRef, newRequest);
    return newRequest;
  } catch (error) {
    logger.error('Error saving product request:', error);
    throw error;
  }
};

// Update product request status
export const updateProductRequestStatus = async (id: string, status: ProductRequest['status']) => {
  try {
    const requestRef = doc(db, 'productRequests', id);
    await setDoc(requestRef, { 
      status, 
      updatedAt: new Date() 
    }, { merge: true });
  } catch (error) {
    logger.error('Error updating product request:', error);
    throw error;
  }
};

// Approve a product request and assign product to customer
export const approveProductRequest = async (id: string) => {
  try {
    logger.log('🚀 Approving request with ID:', id);
    
    // First get the request details
    const requestRef = doc(db, 'productRequests', id);
    const requestDoc = await getDoc(requestRef);
    
    if (requestDoc.exists()) {
      const requestData = requestDoc.data() as ProductRequest;
      logger.log('📋 Request data found:', {
        id: requestData.id,
        customerId: requestData.customerId,
        customerName: requestData.customerName,
        productId: requestData.productId,
        productName: requestData.productName,
        status: requestData.status
      });
      
      // Update request status
      logger.log('🔄 Updating request status to completed...');
      await updateProductRequestStatus(id, 'completed');
      logger.log('✅ Request status updated to completed');
      
      // Assign product to customer
      logger.log('🎯 Assigning product to customer:', {
        customerId: requestData.customerId,
        productId: requestData.productId
      });
      
      const assignmentResult = await assignProductToCustomer(requestData.customerId, requestData.productId);
      logger.log('✅ Product assigned successfully!', assignmentResult);
      
      // Let's verify the assignment was created
      logger.log('🔍 Verifying assignment was created...');
      const verifyAssignments = await getCustomerProducts(requestData.customerId);
      logger.log('📊 Verification result - customer products:', verifyAssignments);
      
      return { success: true, message: 'Request approved and product assigned to customer!' };
    } else {
      logger.error('❌ Request not found with ID:', id);
      throw new Error('Request not found');
    }
  } catch (error) {
    logger.error('💥 Error approving request:', error);
    throw error;
  }
};

// Reject a product request
export const rejectProductRequest = async (id: string) => {
  return updateProductRequestStatus(id, 'cancelled');
};

export interface CustomerProduct {
  id: string;
  customerId: string;
  productId: string;
  assignedAt: Date;
  status: 'active' | 'inactive' | 'removed';
}

// Assign a product to a customer
export const assignProductToCustomer = async (customerId: string, productId: string) => {
  try {
    logger.log('🎯 Starting product assignment:', { customerId, productId });
    
    const assignmentRef = doc(collection(db, 'customerProducts'));
    const newAssignment: CustomerProduct = {
      id: assignmentRef.id,
      customerId,
      productId,
      assignedAt: new Date(),
      status: 'active'
    };
    
    logger.log('📝 New assignment data to save:', newAssignment);
    logger.log('💾 Saving to Firestore collection: customerProducts');
    
    await setDoc(assignmentRef, newAssignment);
    
    logger.log('✅ Assignment saved to Firestore successfully!');
    logger.log('🔗 Document ID:', assignmentRef.id);
    
    // Let's also check if we can read it back immediately
    logger.log('🔍 Reading back the saved assignment...');
    const savedDoc = await getDoc(assignmentRef);
    if (savedDoc.exists()) {
      logger.log('✅ Assignment read back successfully:', savedDoc.data());
    } else {
      logger.log('❌ Could not read back the saved assignment');
    }
    
    return newAssignment;
  } catch (error) {
    logger.error('💥 Error assigning product to customer:', error);
    logger.error('Error details:', error);
    throw error;
  }
};

// Remove a product from a customer
export const removeProductFromCustomer = async (customerId: string, productId: string) => {
  try {
    logger.log('🗑️ removeProductFromCustomer called:', { customerId, productId });
    
    const customerProductsCollection = collection(db, 'customerProducts');
    const q = query(
      customerProductsCollection, 
      where('customerId', '==', customerId),
      where('productId', '==', productId)
    );
    const querySnapshot = await getDocs(q);
    
    logger.log('🔍 Found', querySnapshot.docs.length, 'matching documents to remove');
    
    if (!querySnapshot.empty) {
      // Delete all matching documents (in case there are duplicates)
      const deletePromises = querySnapshot.docs.map(doc => {
        logger.log('🗑️ Deleting document:', doc.id);
        return deleteDoc(doc.ref);
      });
      
      await Promise.all(deletePromises);
      logger.log('✅ Successfully deleted', querySnapshot.docs.length, 'customer product assignment(s)');
    } else {
      logger.log('❌ No customer product assignments found to remove');
    }
  } catch (error) {
    logger.error('💥 Error removing product from customer:', error);
    throw error;
  }
};

// Create customer product assignments (for testing/debugging)
export const createCustomerProductAssignments = async (customerId: string, productId: string) => {
  try {
    logger.log('🔧 Creating customer product assignment:', { customerId, productId });
    
    const assignmentRef = doc(collection(db, 'customerProducts'));
    const newAssignment: CustomerProduct = {
      id: assignmentRef.id,
      customerId,
      productId,
      assignedAt: new Date(),
      status: 'active'
    };
    
    await setDoc(assignmentRef, newAssignment);
    logger.log('✅ Customer product assignment created:', newAssignment);
    return newAssignment;
  } catch (error) {
    logger.error('💥 Error creating customer product assignment:', error);
    throw error;
  }
};

// Get products assigned to a specific customer
export const getCustomerProducts = async (customerId: string): Promise<CustomerProduct[]> => {
  try {
    logger.log('🔍 [UPDATED] Fetching customer products for customer ID:', customerId);
    logger.log('🆕 This is the UPDATED version of getCustomerProducts - v2.0');
    
    // First, let's check if there are ANY customer products in the collection
    const customerProductsCollection = collection(db, 'customerProducts');
    const allProductsQuery = query(customerProductsCollection);
    const allProductsSnapshot = await getDocs(allProductsQuery);
    logger.log('📊 Total customer products in collection:', allProductsSnapshot.docs.length);
    
    if (allProductsSnapshot.docs.length > 0) {
      logger.log('📋 All customer products:');
      allProductsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        logger.log('  - Document ID:', doc.id, 'Data:', {
          customerId: data.customerId,
          productId: data.productId,
          status: data.status,
          assignedAt: data.assignedAt
        });
      });
    } else {
      logger.log('❌ NO CUSTOMER PRODUCTS FOUND IN COLLECTION AT ALL!');
    }
    
    // Now query for this specific customer - try both 'active' and true status
    const q = query(
      customerProductsCollection,
      where('customerId', '==', customerId)
    );
    logger.log('🔎 Querying for customer:', customerId, 'with any status');
    
    const querySnapshot = await getDocs(q);
    logger.log('📈 Customer products query result:', querySnapshot.docs.length, 'documents found');
    
    // Let's also log each document found by the query
    if (querySnapshot.docs.length > 0) {
      logger.log('📋 Documents found by query:');
      querySnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        logger.log(`  ${index + 1}. Document ID: ${doc.id}`);
        logger.log(`     Customer ID: ${data.customerId}`);
        logger.log(`     Product ID: ${data.productId}`);
        logger.log(`     Status: ${data.status} (${typeof data.status})`);
        logger.log(`     Assigned At: ${data.assignedAt}`);
      });
    }
    
    // Filter for active status (both 'active' string and true boolean)
    const activeProducts = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      const status = data.status;
      logger.log('🔍 Checking document:', doc.id, 'Status:', status, 'Type:', typeof status);
      const isActive = status === 'active' || status === true;
      logger.log('✅ Is active?', isActive);
      return isActive;
    });
    
    logger.log('✅ Active products after filtering:', activeProducts.length, 'documents');
    logger.log('🎯 ACTIVE PRODUCTS ONLY:', activeProducts.map(doc => ({ id: doc.id, status: doc.data().status })));
    
    if (activeProducts.length > 0) {
      logger.log('✅ Found ACTIVE customer products:');
      activeProducts.forEach(doc => {
        logger.log('  - Document ID:', doc.id, 'Data:', doc.data());
      });
    } else {
      logger.log('❌ No customer products found for customer:', customerId);
      logger.log('🔍 Let me check if there are any products with different status...');
      
      // Check for products with any status
      const anyStatusQuery = query(
        customerProductsCollection,
        where('customerId', '==', customerId)
      );
      const anyStatusSnapshot = await getDocs(anyStatusQuery);
      logger.log('📊 Products with any status:', anyStatusSnapshot.docs.length);
      
      if (anyStatusSnapshot.docs.length > 0) {
        logger.log('📋 Products with any status:');
        anyStatusSnapshot.docs.forEach(doc => {
          logger.log('  - Document ID:', doc.id, 'Data:', doc.data());
        });
      }
    }
    
    const assignments = activeProducts.map(doc => {
      const data = doc.data();
      logger.log('🔄 Processing assignment:', data);
      return {
        id: doc.id,
        ...data,
        assignedAt: data.assignedAt.toDate()
      };
    }) as CustomerProduct[];
    
    logger.log('🎯 [UPDATED v2.0] Final customer products result:', assignments);
    logger.log('✅ [UPDATED v2.0] Returning', assignments.length, 'customer products');
    return assignments;
  } catch (error) {
    logger.error('💥 Error getting customer products:', error);
    throw error;
  }
};
